import React, { useState, useEffect } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { showSuccess, showError, showWarning } from '../utils/toast';

const StudentApprovals = () => {
  const [pendingStudents, setpendingStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('pending'); // 'pending' or 'all'
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({ total: 0, success: 0, failed: 0, errors: [] });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    city: '',
    state: '',
    country: 'India'
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    city: '',
    state: '',
    country: ''
  });

  useEffect(() => {
    fetchPendingStudents();
    fetchAllStudents();
  }, []);

  const fetchPendingStudents = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(
        collection(db, 'students'),
        where('organizationId', '==', adminId),
        where('approvalStatus', '==', 'pending')
      );
      const querySnapshot = await getDocs(q);
      const studentsList = [];
      querySnapshot.forEach((doc) => {
        studentsList.push({ id: doc.id, ...doc.data() });
      });
      setpendingStudents(studentsList);
    } catch (error) {
      console.error('Error fetching pending students:', error);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(
        collection(db, 'students'),
        where('organizationId', '==', adminId)
      );
      const querySnapshot = await getDocs(q);
      const studentsList = [];
      querySnapshot.forEach((doc) => {
        studentsList.push({ id: doc.id, ...doc.data() });
      });
      // Sort by creation date (newest first)
      studentsList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setAllStudents(studentsList);
    } catch (error) {
      console.error('Error fetching all students:', error);
    }
  };

  const handleApproval = async (studentId, status) => {
    setLoading(true);
    try {
      const studentRef = doc(db, 'students', studentId);
      await updateDoc(studentRef, {
        approvalStatus: status,
        approvedAt: new Date().toISOString(),
        approvedBy: localStorage.getItem('adminEmail')
      });

      showSuccess(`Student ${status === 'approved' ? 'approved' : 'rejected'} successfully!`);
      fetchPendingStudents();
      fetchAllStudents();
    } catch (error) {
      console.error('Error updating student status:', error);
      showError('Failed to update student status');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (student) => {
    setEditingStudent(student);
    setEditFormData({
      name: student.name || '',
      email: student.email || '',
      phoneNumber: student.phoneNumber || '',
      city: student.city || '',
      state: student.state || '',
      country: student.country || 'India'
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    
    if (!editFormData.name || !editFormData.email || !editFormData.phoneNumber) {
      showWarning('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const studentRef = doc(db, 'students', editingStudent.id);
      await updateDoc(studentRef, {
        name: editFormData.name,
        email: editFormData.email,
        phoneNumber: editFormData.phoneNumber,
        city: editFormData.city,
        state: editFormData.state,
        country: editFormData.country,
        updatedAt: new Date().toISOString(),
        updatedBy: localStorage.getItem('adminEmail')
      });

      showSuccess('Student details updated successfully!');
      setShowEditModal(false);
      setEditingStudent(null);
      fetchAllStudents();
      fetchPendingStudents();
    } catch (error) {
      console.error('Error updating student:', error);
      showError('Failed to update student details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (student) => {
    // If student is active or approved, deactivate them; if inactive, approve them
    const newStatus = (student.approvalStatus === 'approved' || student.approvalStatus === 'active') ? 'inactive' : 'approved';
    
    if (!window.confirm(`Are you sure you want to ${newStatus === 'approved' ? 'activate' : 'deactivate'} this student?`)) {
      return;
    }

    setLoading(true);
    try {
      const studentRef = doc(db, 'students', student.id);
      await updateDoc(studentRef, {
        approvalStatus: newStatus,
        statusChangedAt: new Date().toISOString(),
        statusChangedBy: localStorage.getItem('adminEmail')
      });

      showSuccess(`Student ${newStatus === 'approved' ? 'activated' : 'deactivated'} successfully!`);
      fetchAllStudents();
      fetchPendingStudents();
    } catch (error) {
      console.error('Error toggling student status:', error);
      showError('Failed to update student status');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.password || !formData.phoneNumber) {
      showWarning('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showWarning('Please enter a valid email address');
      return;
    }

    // Password validation
    if (formData.password.length < 6) {
      showWarning('Password must be at least 6 characters');
      return;
    }

    // Phone validation
    if (formData.phoneNumber.length < 10) {
      showWarning('Please enter a valid phone number');
      return;
    }

    setLoading(true);

    try {
      const adminId = localStorage.getItem('adminId');
      const adminEmail = localStorage.getItem('adminEmail');
      const orgName = localStorage.getItem('adminOrg');

      // Check if email already exists
      const emailQuery = query(
        collection(db, 'students'),
        where('email', '==', formData.email)
      );
      const emailSnapshot = await getDocs(emailQuery);
      
      if (!emailSnapshot.empty) {
        showError('A student with this email already exists!');
        setLoading(false);
        return;
      }

      // Create Firebase Auth account
      let userId = '';
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        userId = userCredential.user.uid;
        // Sign out immediately to avoid affecting current admin session
        await auth.signOut();
      } catch (authError) {
        if (authError.code === 'auth/email-already-in-use') {
          showError('Email already registered in authentication system!');
        } else {
          showError(`Authentication error: ${authError.message}`);
        }
        setLoading(false);
        return;
      }

      // Create student document with approved status
      const studentData = {
        uid: userId,
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        organizationId: adminId,
        organizationName: orgName,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        approvalStatus: 'approved', // Direct approval by admin, no pending status needed
        registrationMethod: 'admin-added',
        addedBy: adminEmail,
        addedByAdmin: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'students'), studentData);

      showSuccess('Student added and approved successfully!');
      
      // Reset form and close modal
      setFormData({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        city: '',
        state: '',
        country: 'India'
      });
      setShowAddForm(false);
      
    } catch (error) {
      console.error('Error adding student:', error);
      showError('Failed to add student. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCSVTemplate = () => {
    const csvContent = `Name,Email,Password,Phone Number,City,State,Country
Rahul Sharma,rahul.sharma@example.com,Rahul@123,9876543210,Mumbai,Maharashtra,India
Priya Patel,priya.patel@example.com,Priya@456,9123456789,Ahmedabad,Gujarat,India
Arjun Kumar,arjun.kumar@example.com,Arjun@789,9988776655,Bangalore,Karnataka,India
Sneha Reddy,sneha.reddy@example.com,Sneha@321,9876501234,Hyderabad,Telangana,India
Amit Singh,amit.singh@example.com,Amit@654,9123450987,Delhi,Delhi,India`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `student_upload_template_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showSuccess('Template downloaded! Fill in student details and upload.');
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      showError('Please upload a CSV file');
      return;
    }

    setCsvFile(file);
    
    // Read and parse CSV
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          showError('CSV file is empty or invalid');
          return;
        }

        // Skip header row
        const dataLines = lines.slice(1);
        
        setUploadProgress({ total: dataLines.length, success: 0, failed: 0, errors: [] });
        setLoading(true);

        const adminId = localStorage.getItem('adminId');
        const adminEmail = localStorage.getItem('adminEmail');
        const orgName = localStorage.getItem('adminOrg');

        let successCount = 0;
        let failedCount = 0;
        const errors = [];

        for (let i = 0; i < dataLines.length; i++) {
          const line = dataLines[i].trim();
          if (!line) continue;

          // Parse CSV line (handle quotes)
          const values = line.match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g) || [];
          const cleanValues = values.map(v => v.replace(/^"|"$/g, '').trim());

          if (cleanValues.length < 4) {
            errors.push(`Row ${i + 2}: Insufficient data`);
            failedCount++;
            continue;
          }

          const [name, email, password, phoneNumber, city = '', state = '', country = 'India'] = cleanValues;

          // Validate required fields
          if (!name || !email || !password || !phoneNumber) {
            errors.push(`Row ${i + 2}: Missing required fields (Name, Email, Password, or Phone)`);
            failedCount++;
            continue;
          }

          // Password validation
          if (password.length < 6) {
            errors.push(`Row ${i + 2}: Password must be at least 6 characters - ${email}`);
            failedCount++;
            continue;
          }

          // Email validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            errors.push(`Row ${i + 2}: Invalid email format - ${email}`);
            failedCount++;
            continue;
          }

          // Check for duplicate email
          try {
            const emailQuery = query(
              collection(db, 'students'),
              where('email', '==', email)
            );
            const emailSnapshot = await getDocs(emailQuery);
            
            if (!emailSnapshot.empty) {
              errors.push(`Row ${i + 2}: Email already exists - ${email}`);
              failedCount++;
              continue;
            }

            // Create Firebase Auth account
            let userId = '';
            try {
              const userCredential = await createUserWithEmailAndPassword(auth, email, password);
              userId = userCredential.user.uid;
              // Sign out immediately to avoid affecting current admin session
              await auth.signOut();
              // Re-authenticate admin if needed
              // (Admin should already be logged in, just ensuring no conflicts)
            } catch (authError) {
              if (authError.code === 'auth/email-already-in-use') {
                errors.push(`Row ${i + 2}: Email already registered in authentication - ${email}`);
              } else {
                errors.push(`Row ${i + 2}: Auth error - ${authError.message}`);
              }
              failedCount++;
              continue;
            }

            // Create student document
            const studentData = {
              uid: userId,
              name,
              email,
              phoneNumber,
              organizationId: adminId,
              organizationName: orgName,
              city,
              state,
              country,
              approvalStatus: 'approved',
              registrationMethod: 'admin-bulk-upload',
              addedBy: adminEmail,
              addedByAdmin: true,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            };

            await addDoc(collection(db, 'students'), studentData);
            successCount++;
            
          } catch (error) {
            errors.push(`Row ${i + 2}: ${error.message}`);
            failedCount++;
          }

          // Update progress
          setUploadProgress({
            total: dataLines.length,
            success: successCount,
            failed: failedCount,
            errors
          });
        }

        setLoading(false);

        // Show results
        if (successCount > 0 && failedCount === 0) {
          showSuccess(`Successfully added ${successCount} students!`);
          setShowBulkUpload(false);
          setCsvFile(null);
          setUploadProgress({ total: 0, success: 0, failed: 0, errors: [] });
        } else if (successCount > 0 && failedCount > 0) {
          showWarning(`Added ${successCount} students, ${failedCount} failed. Check details below.`);
        } else {
          showError(`Failed to add students. Check the errors below.`);
        }

      } catch (error) {
        console.error('Error parsing CSV:', error);
        showError('Failed to parse CSV file. Please check the format.');
        setLoading(false);
      }
    };

    reader.readAsText(file);
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveView('pending')}
            className={`flex-1 px-6 py-4 font-semibold transition ${
              activeView === 'pending'
                ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Pending Approvals ({pendingStudents.length})
            </span>
          </button>
          <button
            onClick={() => setActiveView('all')}
            className={`flex-1 px-6 py-4 font-semibold transition ${
              activeView === 'all'
                ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <span className="flex items-center justify-center gap-2">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              All Students ({allStudents.length})
            </span>
          </button>
        </div>
      </div>

      {/*Action Buttons */}
      <div className="flex justify-end gap-3">
        <button
          onClick={downloadCSVTemplate}
          className="bg-white border-2 border-indigo-600 text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition flex items-center gap-2"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download CSV Template
        </button>
        <button
          onClick={() => setShowBulkUpload(true)}
          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:bg-green-700 transition flex items-center gap-2"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Bulk Upload (CSV)
        </button>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Student
        </button>
      </div>

      {/* Add Student Form Modal */}
      {showAddForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowAddForm(false);
            setFormData({
              name: '',
              email: '',
              password: '',
              phoneNumber: '',
              city: '',
              state: '',
              country: 'India'
            });
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Form Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Add New Student</h2>
                  <p className="text-indigo-100 text-sm">
                    Organization: {localStorage.getItem('adminOrg')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({
                      name: '',
                      email: '',
                      password: '',
                      phoneNumber: '',
                      city: '',
                      state: '',
                      country: 'India'
                    });
                  }}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Form Body */}
            <form onSubmit={handleAddStudent} className="p-6 space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Students added by admin will have <strong>active status</strong> immediately and can login right away using the email and password you provide.
                </p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter student's full name"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="student@example.com"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 6 characters"
                  required
                  minLength="6"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
                <p className="text-xs text-gray-500 mt-1">Student will use this password to login</p>
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="1234567890"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Location Fields */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="City"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    placeholder="State"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    placeholder="Country"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setFormData({
                      name: '',
                      email: '',
                      phoneNumber: '',
                      city: '',
                      state: '',
                      country: 'India'
                    });
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Adding...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Student
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!loading) {
              setShowBulkUpload(false);
              setCsvFile(null);
              setUploadProgress({ total: 0, success: 0, failed: 0, errors: [] });
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Bulk Student Upload</h2>
                  <p className="text-green-100 text-sm">
                    Upload multiple students using CSV file
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!loading) {
                      setShowBulkUpload(false);
                      setCsvFile(null);
                      setUploadProgress({ total: 0, success: 0, failed: 0, errors: [] });
                    }
                  }}
                  disabled={loading}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition disabled:opacity-50"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <h3 className="font-bold text-blue-900 mb-2">Instructions:</h3>
                <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
                  <li>Download the CSV template using the button above</li>
                  <li>Fill in student details (Name, Email, Password, Phone are required)</li>
                  <li>Password must be at least 6 characters</li>
                  <li>Save the file and upload it below</li>
                  <li>All students will be added with <strong>active status</strong> and can login immediately</li>
                </ol>
              </div>

              {/* CSV Format Info */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">CSV Format:</h4>
                <p className="text-sm text-gray-700 mb-2">Your CSV should have these columns:</p>
                <div className="bg-white rounded p-3 font-mono text-xs text-gray-800 overflow-x-auto">
                  Name, Email, Password, Phone Number, City, State, Country
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  <strong>Required:</strong> Name, Email, Password, Phone Number | <strong>Optional:</strong> City, State, Country
                </p>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Upload CSV File <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-4">
                  <label className="flex-1 flex flex-col items-center px-4 py-6 bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition">
                    <svg className="h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      {csvFile ? csvFile.name : 'Click to select CSV file'}
                    </p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      disabled={loading}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Upload Progress */}
              {uploadProgress.total > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-3">Upload Progress:</h4>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900">{uploadProgress.total}</div>
                      <div className="text-xs text-gray-600">Total</div>
                    </div>
                    <div className="bg-green-50 rounded p-3 text-center">
                      <div className="text-2xl font-bold text-green-600">{uploadProgress.success}</div>
                      <div className="text-xs text-gray-600">Success</div>
                    </div>
                    <div className="bg-red-50 rounded p-3 text-center">
                      <div className="text-2xl font-bold text-red-600">{uploadProgress.failed}</div>
                      <div className="text-xs text-gray-600">Failed</div>
                    </div>
                  </div>

                  {loading && (
                    <div className="flex items-center justify-center gap-2 text-blue-600">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      <span className="text-sm font-semibold">Processing...</span>
                    </div>
                  )}

                  {/* Error List */}
                  {uploadProgress.errors.length > 0 && (
                    <div className="mt-4">
                      <h5 className="font-semibold text-red-900 mb-2 text-sm">Errors:</h5>
                      <div className="bg-red-50 rounded border border-red-200 p-3 max-h-40 overflow-y-auto">
                        <ul className="text-xs text-red-800 space-y-1">
                          {uploadProgress.errors.map((error, index) => (
                            <li key={index}>• {error}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (!loading) {
                      setShowBulkUpload(false);
                      setCsvFile(null);
                      setUploadProgress({ total: 0, success: 0, failed: 0, errors: [] });
                    }
                  }}
                  disabled={loading}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                >
                  {uploadProgress.total > 0 && !loading ? 'Close' : 'Cancel'}
                </button>
                {uploadProgress.total > 0 && !loading && uploadProgress.failed > 0 && (
                  <button
                    onClick={downloadCSVTemplate}
                    className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition"
                  >
                    Download Template Again
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Approvals List */}
      {activeView === 'pending' && pendingStudents.length > 0 && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-xl font-bold text-gray-900">Pending Student Approvals</h3>
            <p className="text-sm text-gray-600 mt-1">Review and approve student registration requests</p>
          </div>

        <div className="divide-y divide-gray-200">
          {pendingStudents.map((student) => (
            <div key={student.id} className="p-6 hover:bg-gray-50 transition">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-indigo-100 p-2 rounded-full">
                      <svg className="h-6 w-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-gray-900">{student.name}</h4>
                      <p className="text-sm text-gray-600">{student.email}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                      <p className="text-sm font-semibold text-gray-900">{student.phoneNumber}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                      <p className="text-sm font-semibold text-gray-900">{student.city}, {student.state}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Registration Method</p>
                      <p className="text-sm font-semibold text-gray-900 capitalize flex items-center gap-1">
                        {student.registrationMethod === 'google' && (
                          <svg className="w-4 h-4" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        )}
                        {student.registrationMethod}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Registered</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {new Date(student.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => handleApproval(student.id, 'approved')}
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Approve
                  </button>
                  <button
                    onClick={() => handleApproval(student.id, 'rejected')}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition disabled:opacity-50 flex items-center gap-2"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* All Students List */}
      {activeView === 'all' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-xl font-bold text-gray-900">All Students</h3>
            <p className="text-sm text-gray-600 mt-1">View and manage all students in your organization</p>
          </div>

          {allStudents.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-gray-600 font-medium">No students found</p>
              <p className="text-gray-500 text-sm mt-2">Add students using the button above</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {allStudents.map((student) => (
                <div key={student.id} className="p-6 hover:bg-gray-50 transition">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`p-2 rounded-full ${
                          (student.approvalStatus === 'approved' || student.approvalStatus === 'active') ? 'bg-green-100' :
                          student.approvalStatus === 'inactive' ? 'bg-gray-100' :
                          student.approvalStatus === 'pending' ? 'bg-yellow-100' :
                          'bg-red-100'
                        }`}>
                          <svg className={`h-6 w-6 ${
                            (student.approvalStatus === 'approved' || student.approvalStatus === 'active') ? 'text-green-600' :
                            student.approvalStatus === 'inactive' ? 'text-gray-600' :
                            student.approvalStatus === 'pending' ? 'text-yellow-600' :
                            'text-red-600'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-lg font-bold text-gray-900">{student.name}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              (student.approvalStatus === 'approved' || student.approvalStatus === 'active') ? 'bg-green-100 text-green-800' :
                              student.approvalStatus === 'inactive' ? 'bg-gray-100 text-gray-800' :
                              student.approvalStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {student.approvalStatus?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{student.email}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                          <p className="text-sm font-semibold text-gray-900">{student.phoneNumber}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {student.city && student.state ? `${student.city}, ${student.state}` : student.city || student.state || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Registration Method</p>
                          <p className="text-sm font-semibold text-gray-900 capitalize">
                            {student.registrationMethod === 'admin-added' ? 'Admin Added' :
                             student.registrationMethod === 'admin-bulk-upload' ? 'Bulk Upload' :
                             student.registrationMethod}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Joined</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {new Date(student.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => handleEditClick(student)}
                        disabled={loading}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition disabled:opacity-50 flex items-center gap-2"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      {(student.approvalStatus === 'approved' || student.approvalStatus === 'active' || student.approvalStatus === 'inactive') && (
                        <button
                          onClick={() => handleToggleStatus(student)}
                          disabled={loading}
                          className={`px-4 py-2 rounded-lg font-semibold transition disabled:opacity-50 flex items-center gap-2 ${
                            (student.approvalStatus === 'approved' || student.approvalStatus === 'active')
                              ? 'bg-orange-600 text-white hover:bg-orange-700'
                              : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          {(student.approvalStatus === 'approved' || student.approvalStatus === 'active') ? 'Deactivate' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && editingStudent && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!loading) {
              setShowEditModal(false);
              setEditingStudent(null);
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Edit Student Details</h2>
                  <p className="text-indigo-100 text-sm">
                    {editingStudent.name} - {editingStudent.email}
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!loading) {
                      setShowEditModal(false);
                      setEditingStudent(null);
                    }
                  }}
                  disabled={loading}
                  className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition disabled:opacity-50"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleUpdateStudent} className="p-6 space-y-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Email cannot be changed once registered. To change email, please deactivate this account and create a new one.
                </p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditFormChange}
                  placeholder="Enter student's full name"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={editFormData.email}
                  readOnly
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={editFormData.phoneNumber}
                  onChange={handleEditFormChange}
                  placeholder="1234567890"
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                />
              </div>

              {/* Location Fields */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={editFormData.city}
                    onChange={handleEditFormChange}
                    placeholder="City"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={editFormData.state}
                    onChange={handleEditFormChange}
                    placeholder="State"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    name="country"
                    value={editFormData.country}
                    onChange={handleEditFormChange}
                    placeholder="Country"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 transition"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    if (!loading) {
                      setShowEditModal(false);
                      setEditingStudent(null);
                    }
                  }}
                  disabled={loading}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Update Student
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentApprovals;
